import { Pagination } from 'antd';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { themeQuartz } from 'ag-grid-enterprise';
import { AgGridReact as AgGridReactComponent } from 'ag-grid-react';
import { useBreakpoint, useFillHeight } from '../../hooks';
import { useTranslation } from '../i18n';
import { usePagination } from './hooks';
import styles from './grid.module.less';
import { locale } from './locale/locale';
import { Empty } from './components/empty';
import { Loading } from './components/loading';
import { useAgThemeParams } from './hooks/configProvider';
import type { IServerSideGetRowsParams } from 'ag-grid-enterprise';
import type { ReactElement, Ref } from 'react';
import type { AgGridProps, AgGridReact, GridReadyEvent } from './types';
import './grid.less';

function AgGridComponent<T>(props: AgGridProps<T>, ref: Ref<AgGridReact | null>): ReactElement {
  const gridRef = useRef<AgGridReactComponent>(null);

  const themeParams = useAgThemeParams();
  const accentColor = themeParams?.accentColor as string ?? '#024FA2';
  const theme = themeQuartz.withParams(themeParams ?? {});
  const { t } = useTranslation();
  const { md } = useBreakpoint();
  // TODO: 使用 ref，支持多个表格
  const [initHeight, recalculateHeight] = useFillHeight('#zf-ag-grid');
  const [height, setHeight] = useState(initHeight);

  useEffect(() => {
    setHeight(md === false ? 500 : initHeight);
  }, [md, initHeight]);
  /**
   * 分页信息
   */
  const pagination = usePagination(gridRef, props.paginationPageSize);

  /**
   * 解构自定义属性 columns, data
   */
  const {
    columns,
    data,
    defaultColDef = { filter: false, editable: false },
    onServerDataLoad,
    onGridReady,
    ...original
  } = props;

  useImperativeHandle(ref, (): AgGridReact | null => {
    if (gridRef.current == null) return null;

    const exposedRef = Object.assign(Object.create(Object.getPrototypeOf(gridRef.current)), gridRef.current, {
      recalculateHeight
    });

    return exposedRef;
  }, [gridRef.current, recalculateHeight]);
  // 包装过的 onGridReady 方法
  // 会先执行传入的 onGridReady 方法。然后设置loading状态，执行 onServerDataLoad 方法
  const warpOnGridReady = (event: GridReadyEvent): void => {
    if (onGridReady != null) {
      onGridReady(event);
    }

    if (onServerDataLoad == null) {
      return;
    }

    event.api.setGridOption('serverSideDatasource', {
      getRows: async (params: IServerSideGetRowsParams<T>) => {
        params.api.setGridOption('loading', true);
        try {
          // 调用 loadServerData 方法
          const result = await onServerDataLoad?.(
            params.request.startRow ?? 0,
            params.api.paginationGetPageSize() ?? 0
          );
          if (result == null) {
            params.api.showNoRowsOverlay();
            return;
          }
          params.success({
            rowData: result.items,
            rowCount: result.totalItems
          });
        } finally {
          params.api.setGridOption('loading', false);
          if (params.api.paginationGetRowCount() === 0) {
            params.api.showNoRowsOverlay();
          } else {
            params.api.hideOverlay();
          }
        }
      }
    });
  };

  return (
    <div
      id='zf-ag-grid'
      className={styles.grid}
      style={{
        height: props.autoHeight === true ? '100%' : `${props.height ?? height}px`,
        padding: props.padding ?? '0 12px',
        backgroundColor: props.backgroundColor ?? '#fff'
      }}
    >
      <div className={styles.table}>
        <AgGridReactComponent<T>
          ref={gridRef}
          theme={theme}
          rowData={data}
          columnDefs={columns}
          defaultColDef={{ ...defaultColDef, cellClass: defaultColDef.cellClass ?? 'cell-ellipsis' }}
          rowSelection={{ mode: 'multiRow', checkboxes: false, headerCheckbox: false }}
          pagination={true}
          suppressPaginationPanel={true}
          paginationPageSize={pagination.pageSize}
          cacheBlockSize={props.rowModelType === 'serverSide' ? pagination.pageSize : undefined}
          onPaginationChanged={pagination.onPaginationChanged}
          localeText={locale.getLocale()} // 国际化
          loading={props.loading} // 加载中标识
          loadingOverlayComponent={() => <Loading color={accentColor} />} // 加载中
          noRowsOverlayComponent={() => <Empty color={accentColor} />} // 空数据
          onGridReady={warpOnGridReady}
          enableCellTextSelection={true}
          {...original}
        />
      </div>
      {props.pagination !== false
        && <div className={styles.pagination}>
          <Pagination
            showTotal={total => t('共 {{total}} 条', { total })}
            hideOnSinglePage={pagination.total === 0}
            pageSizeOptions={props.pageSizeOptions ?? [20, 50, 100]}
            defaultPageSize={pagination.defaultPageSize}
            onChange={pagination.onChange}
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
          />
        </div>
      }
    </div>
  );
}

export const AgGrid = forwardRef(AgGridComponent) as <T>(
  props: AgGridProps<T> & { ref?: Ref<AgGridReact<T> | null> }
) => ReactElement;

export * from './config';
